import classNames from "classnames";
import React, { ComponentPropsWithRef, FC, useRef } from "react";

import { IconName } from "../../icon";
import { IconButton } from "../../icon-button";

import "./bottom-menu-button.css";
import { TransparentButton } from "../../transparent-button";

interface IBottomMenuButtonProps extends Pick<Parameters<typeof IconButton>[0], "id" | "onClick"> {
	isActive?: boolean;
	isDisabled?: boolean;
	iconName?: IconName;
	title?: string;
	className?: string;
	id?: string;
	children?: React.ReactNode;
	onHover?(isHovering: boolean): void;
}

export const BottomMenuButton: FC<IBottomMenuButtonProps> = ({
	onClick,
	onHover,
	className,
	iconName,
	isActive,
	isDisabled,
	title,
	id,
	children,
}) => {
	const extendedClassName = classNames([
		"bottom-menu-button",
		{
			"bottom-menu-button__text": !(iconName || children),
			"bottom-menu-button--active": isActive,
		},
		className,
	]);

	const pointerOverRef = useRef(false);

	// We use onPointerOver instead of onMouseEnter to not rigger it with long touch
	const onPointerOver: ComponentPropsWithRef<"button">["onPointerOver"] = (e) => {
		if (!pointerOverRef.current) {
			if (e.pointerType === "mouse") {
				onHover?.(true);
				pointerOverRef.current = true;
			}
		}
	};
	const onPointerOut: ComponentPropsWithRef<"button">["onPointerOut"] = () => {
		if (pointerOverRef.current) {
			pointerOverRef.current = false;
			onHover?.(false);
		}
	};

	if (iconName || children) {
		return (
			<IconButton
				className={extendedClassName}
				iconName={iconName}
				isActive={isActive}
				onClick={onClick}
				title={title}
				disabled={isDisabled}
				onPointerOver={onPointerOver}
				onPointerOut={onPointerOut}
				id={id}
			>
				{children}
			</IconButton>
		);
	}

	return (
		<TransparentButton
			className={extendedClassName}
			onClick={onClick}
			onPointerOver={onPointerOver}
			onPointerOut={onPointerOut}
			disabled={isDisabled}
			id={id}
		>
			{title}
		</TransparentButton>
	);
};

BottomMenuButton.displayName = "BottomMenuIconButton";
